import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { tokens, Loader } from '@hotjar/hotsauce';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { FEEDBACK } from '@hotjar/webapp/constants/routes';
import { Card } from '@hotjar/webapp/components/Card';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useCurrentSiteId } from '@hotjar/site';
import { useTrackingCodeState } from '@hotjar/webapp/hooks/useTrackingCode';
import { useFeedbackSummary } from '@hotjar/webapp/hooks/useFeedbackSummary';
import { NewEmptyWidget, EMPTY_STATE_KIND, EMPTY_STATE_TOOL } from '../NewEmptyWidget';
import { WidgetGhostButton } from '../WidgetGhostButton';
import { ActiveWidget } from './ActiveWidget';
import { getForYouExperimentWidgetHeaderConfig } from '../../utils';

const Footer = styled.footer`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: ${tokens.spacer.x1pt5} ${tokens.spacer.x2};
  border-top: 1px solid ${tokens.color.ui_border_light};
`;

export function FeedbackCard({
  inForYouExperiment,
}: {
  inForYouExperiment?: boolean;
}): JSX.Element | null {
  const currentSiteId = useCurrentSiteId();
  const siteId = currentSiteId ? Number(currentSiteId) : undefined;
  const { t } = useTranslation();
  const trackingCodeState = useTrackingCodeState();
  const { click } = useTrack();
  const trackClick = (elementName: string) =>
    click(elementName, 'Space Overview Feedback Card', {});
  const { data: summary, isLoading, isError } = useFeedbackSummary(siteId);

  const headerConfig = {
    icon: getForYouExperimentWidgetHeaderConfig('feedback', inForYouExperiment).icon,
    variant: getForYouExperimentWidgetHeaderConfig('feedback', inForYouExperiment).variant,
  };

  if (isLoading) {
    return (
      <Card>
        <Card.Header
          {...headerConfig}
          title={t('activation:dashboard.feedback-card.incoming-feedback')}
        />
        <Card.Body>
          <Loader />
        </Card.Body>
      </Card>
    );
  }

  if (isError || !summary || !siteId) {
    return null;
  }

  const { activeWidgets } = summary;

  return (
    <Card>
      <Card.Header
        {...headerConfig}
        href={siteId ? getRoute(FEEDBACK.RESPONSES_DEFAULT.path, { siteId }) : undefined}
        onClick={() => trackClick('Card Title')}
        title={t('activation:dashboard.feedback-card.incoming-feedback')}
        subtitle={
          trackingCodeState.installed && activeWidgets?.length > 0
            ? t('activation:dashboard.feedback-card.overall-sentiment')
            : undefined
        }
      />
      {!trackingCodeState.installed ? (
        <NewEmptyWidget tool={EMPTY_STATE_TOOL.feedback} kind={EMPTY_STATE_KIND.NO_TC} />
      ) : (
        <>
          {activeWidgets?.length > 0 ? (
            <div>
              {activeWidgets.map((widget, i) => (
                <ActiveWidget
                  widgetPosition={i + 1}
                  key={widget.id}
                  siteId={siteId}
                  widget={widget}
                />
              ))}
              <Footer>
                <WidgetGhostButton
                  as="a"
                  href={getRoute(FEEDBACK.RESPONSES_DEFAULT.path, { siteId })}
                  onClick={() => trackClick('Card Footer Button')}
                >
                  {t('activation:dashboard.feedback-card.footer')}
                </WidgetGhostButton>
              </Footer>
            </div>
          ) : (
            <NewEmptyWidget tool={EMPTY_STATE_TOOL.feedback} kind={EMPTY_STATE_KIND.EMPTY} />
          )}
        </>
      )}
    </Card>
  );
}
